import React, { useState } from 'react';
import { PublicLayout } from '../../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';
import { getAttendeesByEmail } from '../../../services/event.service';
import { sendOtpEmail } from '../../../services/emailService';
import { formatDate } from '../../../utils/dateFormatter';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../hooks/useToast';

const MyTickets: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  // Restauration automatique de l'état de connexion au chargement
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('mytickets_email');
    const savedVerified = localStorage.getItem('mytickets_verified');
    if (savedEmail && savedVerified === 'true') {
      setEmail(savedEmail);
      setOtpVerified(true);
      setOtpSent(false);
      // Optionnel : recharger les tickets si besoin
      getAttendeesByEmail(savedEmail).then(attendees => setTickets(attendees));
    }
  }, []);

  // Génération d'un code OTP à 6 chiffres
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Envoi de l'email avec le code OTP
  const sendOTP = async () => {
    if (!email || !email.includes('@')) {
      setError('Veuillez saisir une adresse email valide');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Générer un OTP
      const generatedOTP = generateOTP();
      
      // Stocker l'OTP dans la session locale (en production, utiliser une méthode plus sécurisée)
      localStorage.setItem(`otp_${email}`, generatedOTP);
      localStorage.setItem(`otp_expiry_${email}`, (Date.now() + 10 * 60 * 1000).toString()); // 10 minutes d'expiration
      
      // Extraire le nom de l'email (partie avant le @)
      const nameFromEmail = email.split('@')[0];
      
      // Envoyer l'email avec l'OTP
      await sendOtpEmail({
        to: email,
        name: email, // Utiliser l'email complet comme nom
        otpCode: generatedOTP
      });
      
      setOtpSent(true);
      showToast('Un code de vérification a été envoyé à votre adresse email', 'success');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code de vérification');
      showToast('Erreur lors de l\'envoi du code de vérification', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Vérification du code OTP
  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Veuillez saisir le code à 6 chiffres');
      return;
    }

    try {
      setVerifying(true);
      setError(null);
      
      // Récupérer l'OTP stocké
      const storedOTP = localStorage.getItem(`otp_${email}`);
      const expiryTime = localStorage.getItem(`otp_expiry_${email}`);
      
      // Vérifier si l'OTP est expiré
      if (!storedOTP || !expiryTime || parseInt(expiryTime) < Date.now()) {
        setError('Le code de vérification a expiré. Veuillez demander un nouveau code.');
        return;
      }
      
      // Vérifier si l'OTP est correct
      if (storedOTP !== otp) {
        setError('Code de vérification incorrect');
        return;
      }
      
      // Récupérer les tickets associés à cet email
      const attendees = await getAttendeesByEmail(email);
      setTickets(attendees);
      
      // Nettoyer les données de vérification
      localStorage.removeItem(`otp_${email}`);
      localStorage.removeItem(`otp_expiry_${email}`);
      
      setOtpVerified(true);
      localStorage.setItem('mytickets_email', email);
      localStorage.setItem('mytickets_verified', 'true');
      showToast('Vérification réussie', 'success');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la vérification');
      showToast('Erreur lors de la vérification', 'error');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Mes Tickets | ERNEST NZOGOUG"
        description="Retrouvez tous vos tickets d'événements sur ERNEST NZOGOUG"
        url="https://nzofoug.com/tickets/my-tickets"
      />
      
      <div className="min-h-screen relative pt-20">
        {/* Background Image avec Overlay */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1500&q=80)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/95 backdrop-blur-[2px]" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          {/* En-tête */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Mes Tickets
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Retrouvez tous vos tickets d'événements en saisissant votre adresse email.
            </p>
          </div>
          
          {!otpVerified ? (
            <div className="max-w-md mx-auto bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 shadow-xl">
              {!otpSent ? (
                <>
                  <h2 className="text-2xl font-semibold text-white mb-4">Accéder à vos tickets</h2>
                  <p className="text-gray-300 mb-6">
                    Saisissez l'adresse email utilisée lors de l'achat de vos tickets.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-1">
                        Adresse email
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-white"
                        disabled={loading}
                      />
                    </div>
                    
                    {error && (
                      <div className="text-red-400 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <button
                      onClick={sendOTP}
                      disabled={loading}
                      className="w-full py-2 px-4 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Envoi en cours...
                        </span>
                      ) : (
                        'Afficher mes tickets'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold text-white mb-4">Vérification</h2>
                  <p className="text-gray-300 mb-6">
                    Un code de vérification à 6 chiffres a été envoyé à <span className="font-medium text-yellow-400">{email}</span>. Veuillez le saisir ci-dessous.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="otp" className="block text-sm font-medium text-gray-200 mb-1">
                        Code de vérification
                      </label>
                      <input
                        type="text"
                        id="otp"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        maxLength={6}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-white text-center text-2xl tracking-widest"
                        disabled={verifying}
                      />
                    </div>
                    
                    {error && (
                      <div className="text-red-400 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={verifyOTP}
                        disabled={verifying}
                        className="w-full py-2 px-4 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {verifying ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Vérification...
                          </span>
                        ) : (
                          'Vérifier'
                        )}
                      </button>
                      
                      <button
                        onClick={() => setOtpSent(false)}
                        disabled={verifying}
                        className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                      >
                        Modifier l'adresse email
                      </button>
                      
                      <button
                        onClick={sendOTP}
                        disabled={loading || verifying}
                        className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                      >
                        Renvoyer le code
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 shadow-xl">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-white">Vos tickets</h2>
                    <p className="text-gray-300">
                      Tickets associés à l'adresse email : <span className="font-medium text-yellow-400">{email}</span>
                    </p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setOtpVerified(false);
                      setOtpSent(false);
                      setOtp('');
                      setTickets([]);
                    }}
                    className="mt-4 md:mt-0 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Utiliser une autre adresse email
                  </button>
                </div>
                
                {tickets.length === 0 ? (
                  <div className="text-center py-10">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-200">Aucun ticket trouvé</h3>
                    <p className="mt-1 text-gray-400">
                      Aucun ticket n'est associé à cette adresse email.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-medium text-yellow-400 mb-2">Instructions pour vos tickets</h3>
                      <ul className="text-sm text-gray-300 list-disc pl-5 space-y-2">
                        <li>Chaque ticket ci-dessous dispose d'un <strong>code QR unique</strong> qui doit être présenté à l'entrée de l'événement</li>
                        <li>Vous pouvez <strong>imprimer</strong> vos tickets ou les présenter sur votre <strong>téléphone</strong></li>
                        <li>Un ticket ne peut être utilisé qu'<strong>une seule fois</strong> et sera marqué comme "Utilisé" après vérification</li>
                        <li>Assurez-vous que l'écran de votre téléphone est suffisamment lumineux pour que le code QR soit lisible</li>
                      </ul>
                    </div>
                    {tickets.map((ticket, index) => (
                      <div key={ticket.id} className="bg-white/5 rounded-lg border border-white/10 overflow-hidden relative">
                        {tickets.length > 1 && (
                          <div className="absolute -top-4 -left-4 w-28 h-28 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-gray-900 font-black flex items-center justify-center z-10 shadow-2xl border-4 border-white/30">
                            <span className="text-5xl">{index + 1}</span>
                          </div>
                        )}
                        <div className="flex flex-col md:flex-row">
                          {/* Image de l'événement */}
                          {ticket.event?.image_url && (
                            <div className="md:w-1/4 h-48 md:h-auto">
                              <img 
                                src={ticket.event.image_url} 
                                alt={ticket.event.title} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          
                          {/* Détails du ticket */}
                          <div className="p-6 flex-1">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 p-4 bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-lg border border-gray-700/50">
                              <div className="mb-2 md:mb-0">
                                <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                                  {ticket.event?.title || 'Événement sans titre'}
                                </h2>
                                {ticket.event?.subtitle && (
                                  <p className="text-gray-300 text-sm mt-1">{ticket.event.subtitle}</p>
                                )}
                              </div>
                              <div className="flex items-center">
                                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                                  ticket.status === 'checked_in' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 
                                  ticket.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' : 
                                  'bg-red-500/20 text-red-300 border border-red-500/30'
                                }`}>
                                  <span className={`w-2 h-2 rounded-full mr-2 ${
                                    ticket.status === 'checked_in' ? 'bg-green-400' : 
                                    ticket.status === 'pending' ? 'bg-yellow-400' : 
                                    'bg-red-400'
                                  }`}></span>
                                  {ticket.status === 'checked_in' ? 'Utilisé' : 
                                   ticket.status === 'pending' ? 'Non utilisé' : 
                                   'Annulé'}
                                </span>
                              </div>
                            </div>
                            

                            
                            {/* QR Code du ticket */}
                            <div className="mt-4">
                              <div className="flex flex-col md:flex-row gap-6">
                                {/* QR Code du ticket - 1/2 de largeur */}
                                <div className="w-full md:w-1/2">
                                  <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-2 border-yellow-500/30 rounded-lg p-8 text-center shadow-lg shadow-yellow-500/5 relative overflow-hidden h-full flex flex-col items-center justify-center">
                                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-transparent opacity-50 z-0"></div>
                                    <div className="relative z-10 w-full flex flex-col items-center">
                                      <a href={`/tickets/${ticket.qr_code}`} className="block w-full">
                                        <div className="bg-white p-5 rounded-lg shadow-md">
                                          <img 
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(ticket.qr_code)}`}
                                            alt="QR Code"
                                            className="w-full h-auto max-w-[250px] mx-auto"
                                          />
                                        </div>
                                        <p className="text-sm text-yellow-300 font-mono bg-black/30 py-2 px-3 rounded break-all mt-6 max-w-full">{ticket.qr_code}</p>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Détails du ticket - 1/2 de largeur */}
                                <div className="w-full md:w-1/2">
                                  <div className="bg-white/5 p-6 rounded-lg h-full">
                                    <p className="text-sm text-gray-300 mb-4 font-medium">Détails du ticket :</p>
                                    <ul className="text-sm text-gray-400 space-y-2">
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Nom :</span>
                                        <span className="text-gray-200">{ticket.attendee_name || 'Non spécifié'}</span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Type :</span>
                                        <span className="text-gray-200">
                                          {ticket.ticket_order?.ticket?.ticket_type === 'standard' ? 'Standard' :
                                          ticket.ticket_order?.ticket?.ticket_type === 'vip' ? 'VIP' :
                                          ticket.ticket_order?.ticket?.ticket_type === 'free' ? 'Gratuit' :
                                          ticket.ticket_order?.ticket?.ticket_type === 'early_bird' ? 'Early Bird' : 'Standard'}
                                        </span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Prix :</span>
                                        <span className="text-gray-200">
                                          {ticket.ticket_order?.ticket?.price ? 
                                            `${ticket.ticket_order.ticket.price} ${ticket.ticket_order.ticket.currency || 'FCFA'}` : 'Gratuit'}
                                        </span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Date :</span>
                                        <span className="text-gray-200">
                                          {ticket.event?.start_date ? formatDate(ticket.event.start_date.toString()) : 'Date non spécifiée'}
                                        </span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Lieu :</span>
                                        <span className="text-gray-200">{ticket.event?.location || 'Lieu non spécifié'}</span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Commande :</span>
                                        <span className="text-gray-200">{ticket.ticket_order?.order_code || 'N/A'}</span>
                                      </li>
                                      <li className="flex">
                                        <span className="text-gray-300 w-24 flex-shrink-0">Email :</span>
                                        <span className="text-gray-200">{ticket.attendee_email || 'Non spécifié'}</span>
                                      </li>
                                      {ticket.attendee_phone && (
                                        <li className="flex">
                                          <span className="text-gray-300 w-24 flex-shrink-0">Téléphone :</span>
                                          <span className="text-gray-200">{ticket.attendee_phone}</span>
                                        </li>
                                      )}
                                    </ul>
                                    <p className="text-sm text-gray-300 mt-4 mb-4">
                                      Statut : <span className={ticket.status === 'checked_in' ? 'text-green-400 font-medium' : 'text-yellow-400 font-medium'}>
                                        {ticket.status === 'checked_in' ? 'Utilisé' : 'Non utilisé'}
                                      </span>
                                    </p>
                                    
                                    {/* Boutons d'action */}
                                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                      <a 
                                        href={`/tickets/${ticket.qr_code}`}
                                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        Voir en plein écran
                                      </a>
                                      <button 
                                        onClick={() => {
                                          const url = `${window.location.origin}/tickets/${ticket.qr_code}`;
                                          if (navigator.share) {
                                            navigator.share({
                                              title: `Ticket pour ${ticket.event?.title || 'Événement'}`,
                                              text: `Voici mon ticket pour ${ticket.event?.title || 'l\'événement'}`,
                                              url: url
                                            }).catch(err => console.error('Erreur lors du partage:', err));
                                          } else {
                                            navigator.clipboard.writeText(url);
                                            showToast('Lien copié dans le presse-papier', 'success');
                                          }
                                        }}
                                        className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                          <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                                        </svg>
                                        Partager
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

// Déconnexion (bouton à placer où vous voulez dans le composant)
const handleLogout = () => {
  setEmail('');
  setOtp('');
  setOtpSent(false);
  setOtpVerified(false);
  setTickets([]);
  localStorage.removeItem('mytickets_email');
  localStorage.removeItem('mytickets_verified');
};

export default MyTickets;
